// ════════════════════════════════════════════════════════════════════════
//  Lizenzrechnung – ohne React, ohne Netz
//
//  Hier steht, was sich rechnen lässt: das Ende eines Zeitraums, der anteilige
//  Betrag, der Zustand einer Lizenz. Absichtlich getrennt von der Komponente.
//  Beim letzten Mal blieb ein Test über das gerenderte Bild grün, während die
//  Anzeige falsch war - die Rechnung war nie einzeln geprüft.
//
//  ZEITZONE: alle Stichtage sind Berliner Datumsgrenzen. Gerechnet wird
//  deshalb NICHT mit `new Date()` und der Zeitzone des Rechners, sondern mit
//  Datumszeichenketten `YYYY-MM-DD`. Ein Datum ohne Uhrzeit hat keine
//  Sommerzeit, und der Vergleich zweier solcher Zeichenketten ist zugleich der
//  Vergleich der Daten.
// ════════════════════════════════════════════════════════════════════════

/** Heute in Europe/Berlin als `YYYY-MM-DD`. */
export function todayInBerlin(now: Date = new Date()): string {
  // "sv-SE" liefert genau YYYY-MM-DD. Der Umweg über die Schwedenschreibweise
  // ist kein Scherz, sondern die kürzeste Form ohne eigene Zusammensetzung -
  // und sie geht nicht über die Zeitzone des Rechners.
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin" }).format(now);
}

/** Tage zwischen zwei Datumszeichenketten. Negativ, wenn `bis` früher liegt. */
export function daysBetween(from: string, until: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${until}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/**
 * Der nächste 31.08. NACH dem Datum.
 *
 * Spiegelt `next_august_31()` in der Datenbank. „Nach“ ist streng gemeint: vom
 * 31.08. aus ist es der 31.08. des Folgejahres, sonst wäre eine Verlängerung
 * ein Zeitraum der Länge null. Laufen die beiden Stellen auseinander, schlägt
 * die Oberfläche einen anderen Endtag vor als der Verlängerungsjob einsetzt.
 */
export function nextAugust31(from: string): string {
  const year = Number(from.slice(0, 4));
  const candidate = `${year}-08-31`;
  return from < candidate ? candidate : `${year + 1}-08-31`;
}

/**
 * Anteiliger Betrag: Jahrespreis × Tage / 365, auf Cent gerundet.
 *
 * Ein Vorschlag für das Formular, keine Regel in der Datenbank. 365 ist fest
 * und nicht „Tage des Schuljahres“: eine Regel, die im Schaltjahr eine andere
 * Zahl liefert, wäre am Telefon nicht erklärbar.
 */
export function proRatedCents(
  annualPriceCents: number | null,
  from: string,
  until: string,
): number | null {
  if (annualPriceCents === null) return null;
  const days = daysBetween(from, until);
  if (days <= 0) return 0;
  return Math.round((annualPriceCents * days) / 365);
}

export type LicenseState = "none" | "expired" | "soon" | "valid";

/**
 * Wie viele Tage vor dem Ablauf eine Lizenz als „läuft bald ab“ gilt.
 *
 * Zwei Monate: die Verlängerung ist ein Gespräch, kein Knopfdruck, und ein
 * Schuljahr endet am 31.08. - wer Anfang Juli sieht, dass es zu Ende geht, hat
 * noch Zeit vor den Ferien. Die Zahl steht an genau dieser Stelle.
 */
export const EXPIRES_SOON_DAYS = 60;

export function licenseState(
  licenseUntil: string | null,
  today: string = todayInBerlin(),
): LicenseState {
  if (licenseUntil === null) return "none";
  if (licenseUntil < today) return "expired";
  return daysBetween(today, licenseUntil) <= EXPIRES_SOON_DAYS ? "soon" : "valid";
}

export const LICENSE_LABEL: Record<LicenseState, string> = {
  none: "keine Lizenz",
  expired: "abgelaufen",
  soon: "läuft bald ab",
  valid: "gültig",
};

const euro = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
});

/** Cent als „240,00 €“. Gedankenstrich, wenn kein Preis vereinbart ist. */
export function formatEuro(cents: number | null): string {
  if (cents === null) return "–";
  return euro.format(cents / 100);
}

/**
 * Eingabe zu Cent. Nimmt „240“, „240,00“ und „240.00“.
 *
 * Gibt `null` zurück, wenn daraus keine Zahl wird - der Aufrufer entscheidet,
 * ob das ein leeres Feld oder ein Fehler ist. Beträge werden als ganze Cent
 * gespeichert, damit die Rundung einmal an einer sichtbaren Stelle passiert
 * und nicht bei jeder Anzeige neu.
 */
export function parseEuroToCents(text: string): number | null {
  const cleaned = text.trim().replace(/\s|€/g, "").replace(",", ".");
  if (cleaned === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

const dateFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/** `YYYY-MM-DD` als „31.08.2027“. */
export function formatDate(iso: string | null): string {
  if (iso === null) return "–";
  return dateFormat.format(new Date(`${iso}T12:00:00Z`));
}
